import { useEffect } from "react";
import { Controller, type SubmitHandler, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { createFileRoute } from "@tanstack/react-router";
import { REGEXP_ONLY_DIGITS } from "input-otp";

import {
  emailCheckAnswerTypeSchema,
  otpVerificationSchema,
} from "@/domains/profile/models/schemas";
import type { OtpVerificationForm } from "@/domains/profile/models/types";
import { useSendOtp } from "@/domains/profile/mutations/use-send-otp";
import { useVerifyOtp } from "@/domains/profile/mutations/use-verify-otp";
import { resetAnswerValue } from "@/domains/profile/utils/formsort";
import { Button, Heading, InputOtp } from "@/ui";
import { validateError } from "@/utils/validation";
import { useCustomQuestionAutoheight } from "./-hooks/use-custom-question-autoheight";
import { useFlowAnswer } from "./-hooks/use-flow-answer";

const OTPVerification = () => {
  const { containerRef } = useCustomQuestionAutoheight();
  const { isPending: isSendingCode, mutate: sendOtp, submittedAt } = useSendOtp();
  const { isPending: isVerifyingCode, mutate: verifyOtp } = useVerifyOtp();
  const address = useFlowAnswer("email_check", emailCheckAnswerTypeSchema)?.address;

  const {
    control,
    formState: { errors, isValid },
    handleSubmit,
    reset,
    setError,
  } = useForm<OtpVerificationForm>({
    mode: "onChange",
    resolver: zodResolver(otpVerificationSchema),
    defaultValues: {
      code: "",
    },
  });

  const isProcessingCode = isSendingCode || isVerifyingCode;

  useEffect(() => {
    if (!address) {
      return;
    }
    sendOtp(
      { email: address },
      {
        onError: (err) => {
          const error = validateError(err);
          if (error) {
            setError("code", {
              message: error.response.data.error.message,
            });
          }
        },
      },
    );
  }, [address, sendOtp, setError]);

  const handleResendCode = () => {
    if (!address) {
      return;
    }
    sendOtp(
      { email: address },
      {
        onSuccess: () => {
          reset();
        },
        onError: (err) => {
          const error = validateError(err);
          if (error) {
            setError("code", {
              message: error.response.data.error.message,
            });
          }
        },
      },
    );
  };

  const handleSkip = () => {
    resetAnswerValue({
      verified: false,
    });
  };

  const onSubmit: SubmitHandler<OtpVerificationForm> = ({ code }) => {
    if (!address) {
      return;
    }
    verifyOtp(
      { email: address, code },
      {
        onSuccess: (verifiedUser) => {
          resetAnswerValue(verifiedUser);
        },
        onError: (err) => {
          const error = validateError(err);
          if (error) {
            setError("code", {
              message: error.response.data.error.message,
            });
          }
        },
      },
    );
  };

  const handleAutoVerify = () => {
    if (isProcessingCode) {
      return;
    }
    handleSubmit(onSubmit)();
  };

  return (
    <div className="flex flex-col gap-4 p-1" ref={containerRef}>
      <Heading className="text-4xl sm:text-4xl" level={2}>
        Stay secure
      </Heading>
      <p>
        We found an existing EO account for this email. To best protect your personal information,
        please enter the 6-digit code we just sent to{" "}
        <span className="font-semibold">{address}</span>.
      </p>
      <Controller
        control={control}
        name="code"
        render={({ field }) => {
          return (
            <>
              <InputOtp.Root
                disabled={isProcessingCode}
                key={submittedAt}
                maxLength={6}
                onChange={field.onChange}
                onComplete={handleAutoVerify}
                pattern={REGEXP_ONLY_DIGITS}
                value={field.value}
                autoFocus
              >
                <InputOtp.Group className="gap-4">
                  {Array.from({ length: 6 }).map((_, index) => {
                    return (
                      <InputOtp.Slot
                        className="h-17.5 w-15 rounded-xl border bg-surface text-center text-2xl first:rounded-l-xl last:rounded-r-xl"
                        index={index}
                        key={index}
                      />
                    );
                  })}
                </InputOtp.Group>
              </InputOtp.Root>
              {errors.code ? (
                <p className="font-semibold text-danger">{errors.code.message}</p>
              ) : null}
            </>
          );
        }}
      />
      <Button
        className="w-full"
        disabled={!isValid || isProcessingCode}
        onClick={handleSubmit(onSubmit)}
        size="lg"
      >
        Continue
      </Button>
      <div className="flex flex-wrap items-center justify-between gap-x-6 gap-y-3">
        <Button
          className="text-content underline underline-offset-4 hover:text-accent"
          disabled={isProcessingCode}
          onClick={handleResendCode}
          variant="focus"
        >
          Resend code
        </Button>
        <Button
          className="text-content underline underline-offset-4 hover:text-accent"
          disabled={isProcessingCode}
          onClick={handleSkip}
          variant="focus"
        >{`I'll skip this for now`}</Button>
      </div>
    </div>
  );
};

export const Route = createFileRoute("/_guest/profiling/_custom-questions/otp")({
  component: OTPVerification,
});
